/**
 * `POST /sym/leaders` — the Leaders panel's numbers (`docs/plans/leader-screener.md`).
 *
 * The open screen's symbols in; ERG Rank, its three legs and the growth box as
 * of a date out, with the session before it. Same service and base URL as
 * `/sym/*`, and `api/seasonality.ts`'s rule: nothing here throws.
 */

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const SYM_URL = (
  httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || ""
).replace(/\/$/, "");

export const leadersConfigured = SYM_URL !== "";

/** One symbol as of the panel's date. Any field may be null: not ranked, no box. */
export interface LeaderRow {
  symbol: string;
  ergRank: number | null;
  epsRank: number | null;
  rsRankI: number | null;
  grpRank: number | null;
  growthBox: number | null;
  /** The close on a past date — absent on Latest. */
  closeAsOf?: number | null;
}

export interface LeaderPrior {
  symbol: string;
  ergRank: number | null;
  growthBox: number | null;
}

export interface LeadersAnswer {
  resolvedDate: string | null;
  priorDate: string | null;
  rows: LeaderRow[];
  prior: LeaderPrior[];
}

export type LeadersResult = { ok: true; answer: LeadersAnswer } | { ok: false; reason: string };

export async function fetchLeaders(
  symbols: readonly string[],
  date: string | null,
  signal?: AbortSignal,
): Promise<LeadersResult> {
  if (!SYM_URL) return { ok: false, reason: "no server configured" };
  try {
    const response = await fetch(`${SYM_URL}/sym/leaders`, {
      method: "POST",
      headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({ symbols, ...(date ? { date } : {}) }),
      signal,
    });
    if (!response.ok) {
      return { ok: false, reason: (await response.text()) || `leaders failed (${response.status})` };
    }
    return { ok: true, answer: (await response.json()) as LeadersAnswer };
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") return { ok: false, reason: "aborted" };
    return { ok: false, reason: cause instanceof Error ? cause.message : "leaders failed" };
  }
}
